import { toast } from "react-hot-toast";
import moment from "moment-timezone";
import { DEFAULT_DATE_FORMAT, DEFAULT_DATETIME_FORMAT, DEFAULT_TIME_FORMAT, DEFAULT_TIMEZONE ,PROJECT_DATE_FORMAT} from "./constants";

export const isNullOrEmpty = (data) => {
  return (
    data === null ||
    data === undefined ||
    data === "" ||
    (Array.isArray(data) && data.length === 0) ||
    (typeof data === "object" && Object?.keys(data).length === 0)
  );
};

const allowedTypes = ["success", "error", "info", "loading"];

export const showToast = (message = "", type = "info", options = {}) => {
  if (!message) return;

  const toastType = allowedTypes.includes(type) ? type : "info";

  toast(message, {
    type: toastType,
    id: options.id || message, // fallback to message if no custom id
    duration: options.duration || 4000,
    position: options.position || "top-center",
    ...options, // spread other custom react-hot-toast options
  });
};

export function serialize(obj) {
  let str = [];
  for (let p in obj) {
    if (obj.hasOwnProperty(p)) {
      if (Array.isArray(obj[p])) {
        obj[p].forEach((item) => {
          str.push(encodeURIComponent(p) + "[]=" + encodeURIComponent(item));
        });
      } else {
        str.push(encodeURIComponent(p) + "=" + encodeURIComponent(obj[p]));
      }
    }
  }
  return str.join("&");
}

export const createOptionListForSelectTag = (
  data = null,
  label = "",
  value = "",
  suffix = "",
  overrideLabel = ""
) => {
  let list = [];

  if (isNullOrEmpty(data) || isNullOrEmpty(label) || isNullOrEmpty(value)) {
    return list;
  }

  if (!Array.isArray(data)) {
    return list;
  }

  list = data?.map((cur) => {
    const labelText =
      isNullOrEmpty(overrideLabel) || isNullOrEmpty(cur[overrideLabel])
        ? cur[label]
        : cur[overrideLabel];

    return {
      value: cur[value],
      label:
        labelText + (isNullOrEmpty(suffix) ? "" : " (" + cur[suffix] + ")"),
    };
  });

  return list;
};

export const isStrongPassword = (password) => {
  // Password must be at least 8 characters long
  if (password.length < 8) return false;
  // Password must contain at least one uppercase letter
  if (!/[A-Z]/.test(password)) return false;
  // Password must contain at least one lowercase letter
  if (!/[a-z]/.test(password)) return false;
  // Password must contain at least one digit
  if (!/[0-9]/.test(password)) return false;
  // Password must contain at least one special character
  return /[!@#$%^&*(),.?":{}|<>]/.test(password);
};

export const capitalizeFirstLetter = (str) => {
  if (!str) return str; // Handle null or undefined strings
  return str.charAt(0).toUpperCase() + str.slice(1);
};

export const capitalize = (str) => {
  if (!str) return str; // Handle null or undefined strings
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

export const getBase64 = (file, maxSizeInMB = 2) => {
  const MAX_FILE_SIZE = maxSizeInMB * 1024 * 1024; // Convert MB to bytes
  return new Promise((resolve, reject) => {
    if (!file) {
      reject(new Error("No file provided"));
      return;
    }
    if (file.size > MAX_FILE_SIZE) {
      reject(new Error(`File size exceeds ${maxSizeInMB}MB limit`));
      return;
    }
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      resolve(reader.result); // base64 string
    };
    reader.onerror = (error) => {
      reject(error);
    };
  });
};

export function getCurrentLanguage() {
  if (typeof window === "undefined") return null;
  const lang = localStorage.getItem("i18nextLng");
  return lang || "en"; // default fallback language
}

export function formatNumber(number = 0, decimals = 2) {
  return Number(number).toLocaleString(undefined, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

export const getAppTz = () => {
  if (typeof window === "undefined") {
    return DEFAULT_TIMEZONE;
  }

  return localStorage.getItem("userTimezone") || DEFAULT_TIMEZONE;
};


export function getCurrentDateTimeAsDateObject(timezone = getAppTz()) {
  return getCurrentDateTime(null, timezone).toDate();
}

export function getCurrentTime(format = DEFAULT_TIME_FORMAT, timezone = getAppTz()) {
  return getCurrentDateTime(format, timezone);
}

export function convertToUserTimezone(
  utcDate = null,
  format = DEFAULT_DATETIME_FORMAT,
  timezone = getAppTz(),
  returnType = "formatted",
) {
  if (returnType === "valueOf") {
    if (!utcDate) return getCurrentDateTime(null, timezone).valueOf();
    return moment.utc(utcDate).tz(timezone).valueOf();
  }

  if (!utcDate) return getCurrentDateTime(format, timezone);
  return moment.utc(utcDate).tz(timezone).format(format);
}

export function getCurrentDate(format = DEFAULT_DATE_FORMAT, timezone = getAppTz()) {
  return getCurrentDateTime(format, timezone);
}

export function ceilToTwoDecimal(num) {
  return Math.ceil(num * 100) / 100;
}

export const getMyTimeZone = () => {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

export function capitalizeWords(text) {
  return text
    .split(" ")
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}


const getStoredTimezone = () => {
  if (typeof window === "undefined") {
    return DEFAULT_TIMEZONE;
  }
 
  return localStorage.getItem("userTimezone") || DEFAULT_TIMEZONE;
};
 
export function getCurrentDateTime(format = DEFAULT_DATETIME_FORMAT, timezone = getStoredTimezone()) {
  const now = moment.tz(timezone || DEFAULT_TIMEZONE);
 
  return format ? now.format(format) : now;
}



export function formatDateAndTime(date , format = PROJECT_DATE_FORMAT){
  return moment(date).format(format);
}


//  To Get Current Date Wise Lot Number
export function makeLotNumber(date = null) {
  if (!date) {
    return getCurrentDate("DDMMYY");
  }

  return moment(date).format("DDMMYY");
}


//  To Get One day Prior Lot Number
export function makePriorLotNumber(date) {
  return moment(date)
    .subtract(1, "day")
    .format("DDMMYY");
}





export function makeShiftWiseLotNumber(date = null) {
  const timezone = getAppTz(); // single source of truth

  const now = moment.tz(timezone);
  const m = date ? moment.tz(date, timezone) : now.clone();
  const newM = now.clone();

  // Apply shift logic only for Mazatlan
  if (timezone === "America/Mazatlan") {
    const isToday = m.isSame(now, "day");

    // If NOT today → no shift logic
    if (!isToday) {
      return m.format("DDMMYY");
    }
    
    // 12:00 AM – 7:59 AM → previous day
    if (newM.hour() < 8) {
      return m.clone().subtract(1, "day").format("DDMMYY");
    }
  }
  return m.format("DDMMYY");
}

export function formatPalletName(palletId, prefix = "P", padLength = 2) {
  if (palletId === null || palletId === undefined || palletId === "") {
    return "";
  }

  return `${prefix}${String(palletId).padStart(padLength, "0")}`;
}

export function isFormattedPalletName(value, prefix = "P", padLength = 2) {
  if (!value) {
    return false;
  }

  const pattern = new RegExp(`^${prefix}\\d{${padLength}}$`, "i");
  return pattern.test(String(value).trim());
}

export function parsePalletCodeFilter(value, prefix = "P", padLength = 2) {
  if (!value) {
    return "";
  }

  const trimmedValue = String(value).trim().toUpperCase();

  if (!isFormattedPalletName(trimmedValue, prefix, padLength)) {
    return "";
  }

  return String(parseInt(trimmedValue.slice(prefix.length), 10));
}

export function normalizePalletCodeFilter(value, prefix = "P", padLength = 2) {
  if (value === null || value === undefined || value === "") {
    return "";
  }

  const trimmedValue = String(value).trim().toUpperCase();

  if (isFormattedPalletName(trimmedValue, prefix, padLength)) {
    return trimmedValue;
  }

  if (/^\d+$/.test(trimmedValue)) {
    return formatPalletName(trimmedValue, prefix, padLength);
  }

  return "";
}


export function ucwords(str) {
  return str.replace(/\b\w/g, char => char.toUpperCase());
}



  export function getTranslatedRoomName (name ,_t) {
    
    if (!name) return "";

    const parts = name.split(" "); // ["Room", "1"]

    const roomWord = _t("room", "Room"); // translated
    const number = parts[1] || "";

    return `${roomWord} ${number}`;
  };


export function getTranslatedOvenName(name, _t) {
  if (!name) return "";

  const parts = name.split(" "); // ["Oven", "1"]

  // console.log(parts)
  const ovenWord = _t(parts[0], "Oven"); // translated
  const number = parts[1] || "";

  return `${ovenWord} ${number}`;
}


export function minWeightForCases(cases , weight){
    const caseWeightMap = {
      1: 57.1,
      2: 87.3,
      3: 117.4,
      4: 147.5,
      5: 177.7,
      6: 207.8,
      7: 237.9,
      8: 268.0,
      9: 298.2,
      10: 328.3,
      11: 358.4,
      12: 388.6,
      13: 418.7,
      14: 448.8,
      15: 479.0,
      16: 509.1,
      17: 539.2,
      18: 569.3,
      19: 599.5,
      20: 629.6,
      21: 659.7,
      22: 689.9,
      23: 720.0,
      24: 750.1,
      25: 780.3,
      26: 810.4,
      27: 840.5,
      28: 870.6,
      29: 900.8,
      30: 930.9,
  };

  const minWeight = caseWeightMap[cases] || 0;

  return {
    'status' : weight >= minWeight ,
    'required_min_weight' : minWeight
  };
}